/**
 * Loads a real Hunspell (the spellchecker LibreOffice and Firefox use),
 * compiled to WebAssembly, with one dictionary per language. Nothing is
 * fetched until a document needs that language; after that the dictionary
 * stays loaded for the session. Spanish takes about 40 ms to set up,
 * Portuguese about 250 ms (its word list is six times bigger).
 */
import type { HunspellFactory } from 'hunspell-asm';
import type { SpellLanguage } from './language';
import type { Speller } from './words';

/** Folder under /dictionaries; bump it when a dictionary file changes. */
export const DICTIONARY_VERSION = 'v1';
const FILE_NAMES: Record<SpellLanguage, string> = { en: 'en', es: 'es', pt: 'pt-BR' };

// Every browser we support has it; TypeScript 4.9's DOM types just predate it
declare const DecompressionStream: { new (format: 'gzip'): TransformStream<Uint8Array, Uint8Array> };

let factory: Promise<HunspellFactory> | null = null;
const ready = new Map<SpellLanguage, Speller>();
const pending = new Map<SpellLanguage, Promise<Speller | null>>();
const failed = new Set<SpellLanguage>();

async function fetchDictionaryFile(name: string): Promise<Uint8Array> {
    const res = await fetch(`/dictionaries/${DICTIONARY_VERSION}/${name}.gz`);
    if (!res.ok) throw new Error(`${name}: HTTP ${res.status}`);
    const bytes = new Uint8Array(await res.arrayBuffer());
    // The files are stored gzipped. If something on the way already unzipped
    // one, the gzip signature (1f 8b) is gone and it's ready as is.
    if (bytes[0] !== 0x1f || bytes[1] !== 0x8b) return bytes;
    const stream = new Blob([bytes]).stream().pipeThrough(new DecompressionStream('gzip'));
    return new Uint8Array(await new Response(stream).arrayBuffer());
}

/** Remembers every answer, since the same words come up on every pass. */
function withMemory(spell: (w: string) => boolean, suggest: (w: string) => string[]): Speller {
    const known = new Map<string, boolean>();
    return {
        spell(word) {
            let answer = known.get(word);
            if (answer === undefined) {
                answer = spell(word);
                known.set(word, answer);
            }
            return answer;
        },
        suggest,
    };
}

async function load(lang: SpellLanguage): Promise<Speller | null> {
    try {
        // The CommonJS build (see src/types/hunspell-asm.d.ts for why), whose
        // exports some bundlers put under `default`
        factory ??= import('hunspell-asm/dist/cjs/index.js')
            .then((m: any) => (m.loadModule ? m : m.default).loadModule());
        const name = FILE_NAMES[lang];
        const [hunspell, aff, dic] = await Promise.all([
            factory,
            fetchDictionaryFile(`${name}.aff`),
            fetchDictionaryFile(`${name}.dic`),
        ]);
        const instance = hunspell.create(
            hunspell.mountBuffer(aff, `${name}.aff`),
            hunspell.mountBuffer(dic, `${name}.dic`),
        );
        const speller = withMemory((w) => instance.spell(w), (w) => instance.suggest(w));
        ready.set(lang, speller);
        return speller;
    } catch (e) {
        console.warn(`Spellcheck: couldn't load the ${lang} dictionary, using the browser's instead`, e);
        failed.add(lang);
        return null;
    } finally {
        pending.delete(lang);
    }
}

/** The dictionary for `lang` if it has loaded, otherwise null. */
export function getSpeller(lang: SpellLanguage): Speller | null {
    return ready.get(lang) ?? null;
}

/** Starts loading `lang` (once) and resolves when it's usable. */
export function loadSpeller(lang: SpellLanguage): Promise<Speller | null> {
    const loaded = ready.get(lang);
    if (loaded) return Promise.resolve(loaded);
    if (failed.has(lang)) return Promise.resolve(null);
    let promise = pending.get(lang);
    if (!promise) {
        promise = load(lang);
        pending.set(lang, promise);
    }
    return promise;
}

/** True once loading `lang` has failed for good (offline, old browser). */
export function dictionaryFailed(lang: SpellLanguage): boolean {
    return failed.has(lang);
}
